import { motion } from "framer-motion";
import { Landmark, Building2, LineChart, Globe2, Home, Briefcase, KeyRound, Coins, Receipt, FileText } from "lucide-react";
import { ReadinessCta } from "./ReadinessCta";

const CORP_IMG =
  "https://images.pexels.com/photos/6918529/pexels-photo-6918529.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RETAIL_IMG =
  "https://images.pexels.com/photos/8730048/pexels-photo-8730048.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const corporate = [
  { icon: Landmark, name: "Term Loans", desc: "Greenfield, expansion & capex funding" },
  { icon: LineChart, name: "Working Capital Demand Loans", desc: "Cash-flow aligned WC structures" },
  { icon: Building2, name: "Non-Convertible Debentures", desc: "Listed & unlisted NCD placements" },
  { icon: Globe2, name: "External Commercial Borrowings", desc: "Cross-border debt, hedged right" },
];

const retail = [
  { icon: Home, name: "Home Loans", desc: "Purchase, resale & balance transfer" },
  { icon: Briefcase, name: "Business Loans", desc: "Unsecured & secured MSME credit" },
  { icon: KeyRound, name: "Loan Against Property", desc: "Unlock equity in your assets" },
  { icon: Coins, name: "Gold Loans", desc: "Instant liquidity against gold" },
  { icon: Receipt, name: "Purchase Invoice Discounting", desc: "Fund supplier payments against invoices" },
  { icon: FileText, name: "Sales Invoice Discounting", desc: "Unlock receivables before due date" },
];

const Card = ({ no, title, img, imgAlt, items, span, testId }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testId}
    className={`group ${span} bg-slate2 border border-white/10 overflow-hidden`}
  >
    <div className="relative h-64 md:h-72 overflow-hidden">
      <img
        src={img}
        alt={imgAlt}
        className="img-spotlight w-full h-full object-cover scale-105 transition-transform duration-700 group-hover:scale-100"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate2 via-transparent to-transparent" />
      <span className="absolute top-5 left-5 font-serif text-4xl text-gold/80">{no}</span>
    </div>
    <div className="p-8">
      <h3 className="font-serif text-2xl sm:text-3xl mb-6">{title}</h3>
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
        {items.map((item) => (
          <li key={item.name} className="flex gap-3">
            <item.icon size={18} className="text-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">{item.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </motion.article>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-14 lg:py-20 bg-slate3/40">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="max-w-2xl mb-16"
      >
        <p className="overline-label mb-4">What we arrange</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
          Ten ways capital finds you.
        </h2>
        <p className="mt-5 text-[#A0AAB2] text-base md:text-lg leading-relaxed">
          One practice, two desks — institutional debt for businesses that are scaling,
          and retail credit for the people behind them.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <Card
          no="01"
          title="Corporate Debt"
          img={CORP_IMG}
          imgAlt="Corporate deal handshake"
          items={corporate}
          span="md:col-span-7"
          testId="services-corporate-card"
        />
        <Card
          no="02"
          title="Retail Lending"
          img={RETAIL_IMG}
          imgAlt="Couple receiving keys to their new home"
          items={retail}
          span="md:col-span-5"
          testId="services-retail-card"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8 }}
        className="mt-10 bg-slate2 border border-white/10 p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
        data-testid="services-readiness-strip"
      >
        <div>
          <h3 className="font-serif text-xl sm:text-2xl">Working capital or business loan on your mind?</h3>
          <p className="text-sm text-muted-foreground mt-1">Take the free readiness check before you apply.</p>
        </div>
        <ReadinessCta testId="services-readiness-cta" />
      </motion.div>

      <div className="mt-8">
        <a data-testid="invoice-discounting-link" href="/invoice-discounting" className="text-gold text-sm tracking-wide hover:text-goldhover transition-colors">
          How invoice discounting works →
        </a>
      </div>
    </div>
  </section>
);
